import {
  AspectRatio,
  Box,
  BoxProps,
  Icon,
  Image,
  Text,
} from "@chakra-ui/react";
import SelectionIndicator from "components/SelectionIndicator";
import SupplyBadge from "components/SupplyBadge";
import TraitImage from "components/TraitImage";
import { publicUrl } from "config";
import { traitCategories } from "config/categories";
import { FaCheck, FaCheckDouble } from "react-icons/fa";
import { ImageSize } from "types";
import { getTraitCategory } from "utils/traitsUtils";

export type TraitCardProps = {
  tokenId: number;
  supply?: [current: number, max: number];
  imageSize?: ImageSize;
  canvasBg?: BoxProps["bg"];
  selectionIndicatorColor?: BoxProps["color"];
  shapeOpacity?: number;
  isSelected?: boolean;
  isDisabled?: boolean;
  isVisible?: boolean;
  isActive?: boolean;
  onClick?: (tokenId: number) => void;
} & Omit<BoxProps, "onClick">;

const TraitCard: React.FC<TraitCardProps> = ({
  tokenId,
  supply,
  imageSize,
  canvasBg = "dark.400",
  shapeOpacity,
  children,
  isSelected = false,
  isDisabled = false,
  isVisible = true,
  isActive = false,
  bg = "dark.700",
  onClick,
  ...rest
}) => {
  const category = getTraitCategory(tokenId);

  return (
    <Box
      bg={bg}
      p="1.5"
      w="full"
      outline="2px solid"
      userSelect="none"
      borderRadius="lg"
      outlineColor={
        isActive ? "primary.500" : isSelected ? "bright.50" : "transparent"
      }
      cursor={isDisabled ? "not-allowed" : onClick ? "pointer" : undefined}
      transition="all 0.15s"
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      overflow="hidden"
      onClick={() => {
        if (!isDisabled) {
          onClick?.(tokenId);
        }
      }}
      {...rest}
    >
      <Box position="relative">
        <SelectionIndicator
          position="absolute"
          top="-1.5"
          left="-1.5"
          w="8"
          h="8"
          bg={bg}
          color={isActive ? "primary.500" : "bright.50"}
          icon={
            isActive && isSelected ? (
              <Icon as={FaCheckDouble} w={3.5} h={3.5} />
            ) : (
              <Icon as={FaCheck} w={3.5} h={3.5} />
            )
          }
          isSelected={isActive || isSelected}
        />

        <AspectRatio
          ratio={1}
          w="full"
          bg={canvasBg}
          borderRadius="md"
          userSelect="none"
        >
          <>
            <Image
              src={`${publicUrl}/v2-placeholder_transparent.png`}
              w="full"
              transition="opacity 0.15s"
              position="absolute"
              zIndex={category === traitCategories[0] ? 2 : 0}
              opacity={shapeOpacity}
            />
            {isVisible && (
              <TraitImage
                tokenId={tokenId}
                size={imageSize}
                position="absolute"
                zIndex={1}
              />
            )}
          </>
        </AspectRatio>

        {supply && (
          <SupplyBadge
            position="absolute"
            size="sm"
            bottom="0"
            right="0"
            transform="translateY(50%)"
            current={supply[0]}
            max={supply[1]}
            zIndex={3}
            borderRightRadius="none"
          />
        )}
      </Box>

      <Box pt="1.5">
        {children || (
          <Text
            fontSize="sm"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            fontWeight="semibold"
          >
            #{tokenId}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default TraitCard;
